import React, {
  type ComponentType,
  forwardRef,
  type ForwardRefExoticComponent,
} from 'react';
import {isForwardRef} from 'react-is';

import {Profiler} from './Profiler';

type ProfileComponentOptions = {
  id: string;
};

/**
 * This function takes a component render fn (or forwarded ref, or class
 * component) and wraps it in a `Profiler` component, described elsewhere. It
 * returns an identical React component to what was provided, forwarding any
 * refs if appropriate. It should be possible to just wrap any given component
 * in this function, give it a metric name, and the result is a drop-in
 * replacement of the input component.
 *
 * @see Profiler
 */
export function profileComponent<Props extends {}>(
  Component: ComponentType<Props> | ForwardRefExoticComponent<Props>,
  {id}: ProfileComponentOptions
): typeof Component {
  function ProfiledComponent(props: Props, ref?: any) {
    return (
      <Profiler id={id}>
        <Component {...props} ref={ref} />
      </Profiler>
    );
  }
  ProfiledComponent.defaultProps = Component.defaultProps as any;
  ProfiledComponent.displayName = `WandbProfiler__${id}`;

  // @ts-expect-error -- this should work just fine, not sure why TS complains,
  //                     i suspect it's a problem with @types/react and not what
  //                     is happening in this function...
  return isForwardRef(Component)
    ? forwardRef<any, Props>(ProfiledComponent)
    : ProfiledComponent;
}
